import React from "react";

import { FormattedAmount } from "@ui-kit/components/index.es";

import { getDebitAccountTicketLabel } from "util/accounts";
import * as config from "util/config";
import * as i18n from "util/i18n";
import { getTransferTaxNote, hasSchedulerContent } from "util/transferConfirmationNotes";
import { getGeneralTicketItem } from "util/transaction";
import getScheduledDate from "util/transferScheduled";

/**
 * Builds the display-ready `TransferDataInput` consumed by the UI-kit `TransferConfirmation` and
 * `TransferTicket`. The kit is data agnostic, so every account, amount, CBU and document value is
 * formatted here exactly as the legacy `TransferSummaryData` / `TransferTransactionData` screens do.
 */

const TRANSFER_KIND_LABEL_KEYS = {
    same: "transfers.titular.same.label",
    other: "transfers.titular.other.label",
};

/** Legacy renders the scheduling message only on these statuses. */
const STATUSES_WITH_SCHEDULER = ["PENDING", "SCHEDULED"];

/** Debit/credit display: `****1234 CA ARS` (Inc 9.1 / 9.2). */
export function getAccountDescription(account) {
    return getDebitAccountTicketLabel(account);
}

/** Legacy shows the beneficiary account masked, keeping only the middle block of the CBU. */
function getMaskedCbu(cbu) {
    return typeof cbu === "string" && cbu.length > 0 ? cbu.slice(-14, -1) : "";
}

/** True when the value is a 22-digit CBU/CVU (stored as credit id on external-own sends). */
function isCbuOrCvuString(value) {
    return typeof value === "string" && /^\d{22}$/.test(value);
}

/**
 * TODAY submit path often leaves `scheduler: {}` (spread of null). That must not render as
 * "Fecha de transacción".
 */

/**
 * Credit row label. Internal CMF uses account mask; external-own / alias flows may only expose CBU
 * on `creditAccount` or `creditAccountDetail` after preview.
 */
function resolveCreditAccountLabel(internalTransfer, creditAccount, cbu, creditAccountDetail) {
    const cbuCandidate =
        cbu ||
        creditAccountDetail?.cbu ||
        (creditAccount && typeof creditAccount === "object" ? creditAccount.cbu : null);

    if (!internalTransfer) {
        return getMaskedCbu(cbuCandidate) || (isCbuOrCvuString(creditAccount) ? getMaskedCbu(creditAccount) : null);
    }

    const fromAccount = getAccountDescription(creditAccount);
    if (fromAccount) {
        return fromAccount;
    }

    if (creditAccount?.externalOwn || creditAccount?.accountNumber) {
        return (
            getMaskedCbu(creditAccount.cbu) ||
            creditAccount.accountNumber ||
            creditAccount.localBank ||
            creditAccount.recipientName ||
            null
        );
    }

    if (isCbuOrCvuString(creditAccount)) {
        return getMaskedCbu(creditAccount);
    }

    return getMaskedCbu(cbuCandidate) || null;
}

/** First 8 characters, uppercased — the receipt number shown across transfer screens. */
export function getReceiptNumber(id) {
    return typeof id === "string" && id.length > 0 ? id.substring(0, 8).toUpperCase() : "";
}

/**
 * Document of the beneficiary. Legacy labels the row "CUIT/CUIL" but reads only `cuil`, leaving it
 * blank for beneficiaries identified by `cuit`; here both are honoured with their matching prefix.
 */
function getDocumentLabel(clientUser) {
    if (clientUser?.cuil) {
        return `${i18n.get("transferSummary.cuil.input.label")} ${clientUser.cuil}`;
    }
    if (clientUser?.cuit) {
        return `${i18n.get("transferSummary.cuit.input.label")} ${clientUser.cuit}`;
    }
    return null;
}

function getTransferKindLabel(transferKind) {
    const key = TRANSFER_KIND_LABEL_KEYS[transferKind];
    return key ? i18n.get(key) : null;
}

function getConceptLabel(concept) {
    return concept ? `${concept} - ${i18n.get(`transfers.concept.${concept}`)}` : null;
}

/**
 * Resolves transfer category when confirmation has no idActivity yet.
 *
 * @param {string|undefined} transferGroup
 * @param {boolean} internalTransfer
 * @returns {"toContact"|"internal"|"thirdParties"}
 */
function resolveTransferCategoryWithoutActivity(transferGroup, internalTransfer) {
    if (transferGroup === "toContact") {
        return "toContact";
    }
    return internalTransfer ? "internal" : "thirdParties";
}

function getAmountNode(summary) {
    const amount = summary?.amount?.quantity ?? summary?.amount;
    if (amount === undefined || amount === null || amount === "") {
        return null;
    }
    const currency = summary?.currency?.toString() || summary?.amount?.currency?.toString() || "";
    return <FormattedAmount amount={amount} currency={i18n.get(`currency.label.${currency}`, currency)} />;
}

/**
 * Maps the Redux `transfer.summary` into the UI-kit transfer DTO.
 *
 * @param {object} params
 * @param {object} params.summary - Redux `transferSelectors.getSummary`.
 * @param {boolean} params.internalTransfer - Transfer between the customer's own accounts.
 * @param {boolean} [params.scheduledCancel] - Cancellation of a previously scheduled transfer.
 * @param {boolean} [params.isCVU] - Beneficiary account is a CVU (swaps CBU/Bank labels).
 * @param {string} [params.transferGroup] - `toContact` / other; used when confirmation has no idActivity.
 * @param {boolean} [params.forConfirmation] - Pre-send review: drops payer and failure rows.
 * @param {object} [params.activeEnvironment] - Session environment; `name` is the Ordenante on tickets.
 * @returns {object} `TransferDataInput`
 */
export function buildTransferDataInput({
    summary,
    internalTransfer,
    scheduledCancel = false,
    isCVU = false,
    transferGroup = "",
    forConfirmation = false,
    activeEnvironment,
}) {
    if (!summary) {
        return {};
    }

    const {
        idActivity,
        idTransaction,
        voucher,
        idTransactionStatus,
        idTransactionToCancel,
        debitAccount,
        creditAccount,
        creditAccountDetail,
        clientUser,
        cbu,
        alias,
        transferKind,
        concept,
        reference,
        creditReference,
        scheduler,
        tadTrx,
        sendEmail,
        email,
        emailText,
        submitDateTime,
        creationDate,
        creationDateTime,
        creatorFullName,
        signatures,
        pspEntityName,
        responseCVUCode,
        responseCVUDescription,
        category: explicitCategory,
    } = summary;

    const cvuEnabled = config.getBoolean("transfer.cvu.enabled");
    const showsCvuLabels = cvuEnabled && Boolean(isCVU);
    const hasBeneficiary = !internalTransfer && !scheduledCancel;

    // Legacy suppresses the beneficiary block on pending internal transfers because the credit
    // account has not been resolved yet.
    const isInternalPending = idTransactionStatus === "PENDING" && internalTransfer;

    const creditAccountLabel = resolveCreditAccountLabel(internalTransfer, creditAccount, cbu, creditAccountDetail);
    // Internal send persists `creditReference`; third parties use `reference`. Ticket loads both.
    const resolvedReference = reference ?? creditReference ?? null;

    const showCvuFailureReason =
        !forConfirmation &&
        idTransactionStatus === "FAILED" &&
        !config.getBoolean("frontend.show.failedVoucher.transfersFunctionalities", false);

    // On the review screen the transaction does not exist yet, so there is no status to filter by:
    // legacy shows the recurrence whenever one was picked (and only when the payload has content).
    const showScheduler =
        hasSchedulerContent(scheduler) &&
        !tadTrx &&
        (forConfirmation || STATUSES_WITH_SCHEDULER.includes(idTransactionStatus));

    // Confirmation: no operation date. Ticket: hide date on pending; show on FINISHED/FAILED/etc.
    const hideCreationDate =
        forConfirmation || idTransactionStatus === "PENDING" || idTransactionStatus === "PENDING_TOKEN";
    const resolvedCreationDate = hideCreationDate ? null : submitDateTime ?? creationDate ?? creationDateTime ?? null;

    // Confirmation has no idActivity yet; UI-kit category defaults to "internal" and would hide
    // third-party / contact beneficiary rows unless we set it explicitly.
    const resolvedCategory =
        explicitCategory ||
        (!idActivity ? resolveTransferCategoryWithoutActivity(transferGroup, internalTransfer) : undefined);

    return {
        idActivity,
        idTransaction,
        voucher: voucher ?? summary.data?.voucher ?? null,
        idTransactionStatus,
        creationDate: resolvedCreationDate,
        category: resolvedCategory,
        isCVU: showsCvuLabels,
        tadTrx: Boolean(tadTrx),

        idTransactionToCancelLabel: scheduledCancel ? getReceiptNumber(idTransactionToCancel) : null,
        payerLabel: !forConfirmation && idTransactionStatus !== "PENDING" ? activeEnvironment?.name ?? null : null,

        debitAccountLabel: getAccountDescription(debitAccount),
        creditAccountLabel,
        transferKindLabel: hasBeneficiary ? getTransferKindLabel(transferKind) : null,
        cbuOrCvu: hasBeneficiary ? creditAccountDetail?.cbu ?? cbu ?? null : null,
        cuitLabel: isInternalPending ? null : getDocumentLabel(clientUser),
        aliasLabel: hasBeneficiary ? alias ?? null : null,
        owner: hasBeneficiary ? clientUser?.firstName ?? null : null,
        bankOrEntityLabel: isInternalPending
            ? null
            : (showsCvuLabels ? pspEntityName : creditAccountDetail?.bankName) ?? null,

        amountNode: getAmountNode(summary),
        conceptLabel: hasBeneficiary ? getConceptLabel(concept) : null,
        scheduler: showScheduler ? getScheduledDate(scheduler) || null : null,
        // Confirmation always pairs amount | reference (legacy shows the row even when empty).
        reference: forConfirmation ? resolvedReference || "\u00A0" : resolvedReference,

        cvuResponseLabel: showCvuFailureReason ? responseCVUDescription ?? null : null,
        cvuResponseCodeLabel: showCvuFailureReason ? responseCVUCode ?? null : null,

        email: sendEmail ? email ?? null : null,
        emailText: sendEmail ? emailText ?? null : null,

        creatorFullName: creatorFullName ?? null,
        signatures: signatures ?? [],
    };
}

/**
 * Rows for the downloadable/shareable PDF. The generic ticket exporter needs plain values, so it
 * cannot reuse the React nodes from {@link buildTransferDataInput} and mirrors it field by field.
 *
 * @returns {Array<object>} Items produced by `getGeneralTicketItem`.
 */
export function buildTransferTicketPdfBody({
    summary,
    internalTransfer,
    scheduledCancel = false,
    isCVU = false,
    activeEnvironment,
}) {
    if (!summary) {
        return [];
    }

    const data = buildTransferDataInput({
        summary,
        internalTransfer,
        scheduledCancel,
        isCVU,
        activeEnvironment,
    });
    const { idTransactionStatus } = summary;
    const rows = [];

    const pushRow = (key, value, type = "string") => {
        if (value === null || value === undefined || value === "") {
            return;
        }
        rows.push(getGeneralTicketItem(key, value, type));
    };

    if (idTransactionStatus === "FINISHED") {
        pushRow("transferSummary.date.label", summary.submitDateTime, "datefulltime");
        pushRow("transferConfirmation.voucher.label", summary.voucher);
    } else {
        pushRow("transferConfirmation.idTransaction.label", getReceiptNumber(summary.idTransaction));
    }

    pushRow("scheduled.transactions.cancel.idTransactionToCancel.label", data.idTransactionToCancelLabel);
    pushRow("transferSummary.payer.label", data.payerLabel);
    pushRow("transfers.debitAccount.label", data.debitAccountLabel);
    pushRow("transfers.creditAccount.label", data.creditAccountLabel);
    pushRow("transfers.transferKind.label", data.transferKindLabel);
    pushRow(data.isCVU ? "transferSummary.cvu.label" : "transferSummary.cbu.label", data.cbuOrCvu);
    pushRow("transferSummary.cuit.label", data.cuitLabel);
    pushRow("transferSummary.alias.label", data.aliasLabel);
    pushRow("transferSummary.owner.label", data.owner);
    pushRow(data.isCVU ? "transferSummary.entity.label" : "transferSummary.bank.label", data.bankOrEntityLabel);

    const amount = summary.amount?.quantity ?? summary.amount;
    const currency = summary.currency?.toString() || summary.amount?.currency?.toString() || "";
    if (amount !== undefined && amount !== null && amount !== "") {
        rows.push(
            getGeneralTicketItem("transfers.amount.label", amount, `amount${i18n.get(`currency.label.${currency}`)}`),
        );
    }

    pushRow("transfers.concept.label", data.conceptLabel);
    if (!data.tadTrx) {
        pushRow("forms.confirmation.scheduler", data.scheduler);
    }
    pushRow("transfers.reference.label", scheduledCancel ? null : data.reference);
    pushRow("transfers.cvu.response.label", data.cvuResponseLabel);
    pushRow("transfers.cvu.code.label", data.cvuResponseCodeLabel);
    pushRow("transfers.email.label", data.email);
    pushRow("transfers.emailText.label", data.emailText);

    return rows;
}

/**
 * Deferred transfers (TAD) close the ticket with an explanatory note, except when the transfer failed.
 *
 * @returns {string|null} Resolved message, or `null` when the note does not apply.
 */
export function getTadNote(summary, internalTransfer) {
    if (!summary?.tadTrx || summary.idTransactionStatus === "FAILED") {
        return null;
    }
    return i18n.get(
        internalTransfer
            ? "transfer.TAD.internalTransfer.success.message"
            : "transfer.TAD.externalTransfer.success.message",
    );
}

/**
 * Informative notes rendered below the detail on the review screen (tax warning, TAD disclaimer).
 *
 * @returns {string[]} Messages to show, already resolved.
 */
export function getTransferConfirmationNotes(summary, internalTransfer, accounts) {
    return [getTransferTaxNote(summary, internalTransfer, accounts), getTadNote(summary, internalTransfer)].filter(
        Boolean,
    );
}

function getTicketTaxNote(summary, internalTransfer) {
    if (summary?.idTransactionStatus === "FAILED") {
        return null;
    }
    return getTransferTaxNote(summary, internalTransfer, summary?.accounts);
}

/**
 * Informative notes on V3 ticket (PENDING/FINISHED), matching legacy `TransferTransactionData` tax rules.
 *
 * @returns {string[]} Messages to show, already resolved.
 */
export function getTransferTicketNotes(summary, internalTransfer) {
    return [getTicketTaxNote(summary, internalTransfer), getTadNote(summary, internalTransfer)].filter(Boolean);
}
